import * as React from 'react';
import { GridColDef } from '@mui/x-data-grid-pro';

import { checkForPermission, Permission } from 'utils/permissions';
import { ContainerCell } from './ContainerCell';
import { DetentionCell } from './DetentionCell';
import { PickupCell } from './pickup-cell';
import { StateCell } from './state-cell';
import { HoldsCell } from './HoldsCell';

import {
  ACTIONS_CELL_WIDTH,
  ACTIONS_CELL_WIDTH_NO_PERMS,
  CONTAINER_CELL_WIDTH,
  DUAL_RETURNS_CELL_WIDTH,
  HOLDS_CELL_DEFAULT_WIDTH,
  HOLDS_CELL_MIN_WIDTH,
  PICKUP_CELL_WIDTH,
  RETURN_CELL_WIDTH,
} from '../../../config';
import { LastFreeDayCell } from './LastFreeDayCell';
import { HoldsTableHeader, TableHeader } from './TableHeader';
import { EmptyReturnLocationCell } from './empty-return-location-cell';
import { ActionsCell } from './ActionsCell';
import { ReturnCell } from './return-cell';
import { CustomerCell } from './CustomerCell';

/** Base column definition use by all cells (can be overriden per cell) */
const DefaultColDef: Partial<GridColDef> = {
  renderHeader: TableHeader,
  sortable: false,
  width: CONTAINER_CELL_WIDTH,
  cellClassName: 'bg-white border-r border-blue-300',
  disableColumnMenu: true,
};

/**
 * Return the list of visible columns for the track and trace container table;
 * this can depend on factors including the user's permissions
 */
export const getColumns: (permissions: Permission[]) => GridColDef[] = (permissions) => {
  const columns: (GridColDef | null)[] = [
    {
      ...DefaultColDef,
      field: 'containerNumber',
      headerName: 'Container',
      width: 200,
      renderCell: (params) => <ContainerCell {...params} />,
    },
    {
      ...DefaultColDef,
      field: 'state',
      headerName: 'Current State',
      width: 160,
      renderCell: (params) => <StateCell {...params} />,
    },
    {
      ...DefaultColDef,
      field: 'holds',
      headerName: 'Holds to Clear',
      renderCell: (params) => <HoldsCell {...params} />,
      width: HOLDS_CELL_DEFAULT_WIDTH,
      minWidth: HOLDS_CELL_MIN_WIDTH,
      renderHeader: HoldsTableHeader,
    },
    {
      ...DefaultColDef,
      field: 'lastFreeDay',
      headerName: 'Last Free Day',
      renderCell: (params) => <LastFreeDayCell {...params} />,
    },
    {
      ...DefaultColDef,
      width: PICKUP_CELL_WIDTH,
      field: 'pickup',
      headerName: 'Pickup To Schedule',
      renderCell: (params) => <PickupCell {...params} />,
    },
    {
      ...DefaultColDef,
      field: 'emptyReturn',
      headerName: 'Empty To Return',
      renderCell: (params) => <ReturnCell {...params} />,
      width: RETURN_CELL_WIDTH,
    },
    {
      ...DefaultColDef,
      field: 'detention',
      headerName: 'Detention Period',
      // Open Issue: https://github.com/mui/mui-x/issues/1402
      renderCell: (params) => <DetentionCell {...params} />,
    },
    {
      ...DefaultColDef,
      field: 'dualReturns',
      headerName: 'Terminals Open for Dual Returns',
      width: DUAL_RETURNS_CELL_WIDTH,
      renderCell: (params) => <EmptyReturnLocationCell {...params} />,
    },
    {
      ...DefaultColDef,
      field: 'customerTag',
      headerName: 'Customer ID',
      renderCell: (params) => <CustomerCell {...params} />,
    },
    {
      ...DefaultColDef,
      width: checkForPermission([Permission.TrackNTracePickupTrackChanges], permissions)
        ? ACTIONS_CELL_WIDTH
        : ACTIONS_CELL_WIDTH_NO_PERMS,
      field: 'actions',
      renderCell: (params) => <ActionsCell {...params} />,
    },
  ];

  return columns.filter((c) => c !== null) as GridColDef[];
};
